package ui.campaign.detailv2.editor

import androidx.compose.runtime.*
import api.AppType
import api.CampaignState
import api.CampaignType
import api.VisualProperties
import common.frontend.api.Io
import common.frontend.api.canLayerBottomView
import io.Repository
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import ui.campaign.detailv2.editor.feature.FeatureCounter
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.snackBarStateGlobal

@VM
class CampaignEditorVM : NavigationViewModel() {

    lateinit var scopeUi: CampaignEditorScope

    var showLoader by mutableStateOf(false)
        private set

    var state by mutableDataState<CampaignWrapperE>()
        private set

    var tabs by mutableStateOf<List<Tab>>(listOf())
        private set

    var tabIndex by mutableStateOf(0)
        private set

    sealed class Tab(val name: StringResource) {
        object General00 : Tab(Res.string.layer_top_detail_editor__page_general)
        object Location01 : Tab(Res.string.layer_top_detail_editor__page_location)
        object Visual02 : Tab(Res.string.layer_top_detail_editor__page_visual)
        object Offset03 : Tab(Res.string.layer_top_detail_editor__page_offsets)
        object DynamicVisual04 : Tab(Res.string.layer_top_detail_editor__page_dynamic_visual)
        object Effect05 : Tab(Res.string.layer_top_detail_editor__page_effects)
        object Application06 : Tab(Res.string.layer_top_detail_editor__page_application)
        object MiniApp07 : Tab(Res.string.layer_top_detail_editor__page_miniapp)
        object Summary08 : Tab(Res.string.layer_top_detail_editor__page_summary)
    }

    suspend fun resizeDuration(input: Io.Campaign.ResItem, newDuration: kotlin.time.Duration): Io.Campaign.ResItem {
        val newSpecs = VisualProperties(
            type = input.type,
            name = "${input.name}, $newDuration",
            widthIn = input.width,
            heightIn = input.height,
            durationMsIn = input.duration,
            deleteOrigin = false,
            durationMsOut = newDuration.inWholeMilliseconds
        )

        input.resourceId = Repository.api.resource.transform(
            companyId = scopeUi.args.companyId,
            resourceId = input.resourceId,
            props = newSpecs
        ).id

        input.duration = newSpecs.durationMsOut
        input.name = newSpecs.name
        return input
    }

    sealed interface Action {
        data object Save : Action
        data object PageNext : Action
        data object SkipToSummary : Action
        data object Activate : Action
        data object ActivateDraft : Action
        data object Change : Action
        data object ChangeDraft : Action
        data class PageExact(val index: Int) : Action
        companion object {
            @Composable
            fun rememberAction(vm: CampaignEditorVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            Save -> vm.save()
                            Activate -> vm.activate()
                            ActivateDraft -> vm.activateDraft()
                            Change -> vm.change()
                            ChangeDraft -> vm.changeDraft()
                            PageNext -> vm.goToNextPage()
                            SkipToSummary -> vm.skipToSummary()
                            is PageExact -> {
                                vm.goToPage(act.index)

                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * only for new campaigns, only create
     */
    fun save() {
        showLoader = true
        scope.launch {
            runCatching {
                state.entry?.extract()?.also {
                    Repository.api.campaign.create(scopeUi.args.companyId, it)
                } ?: error("Invalid state of app, refresh required")
            }.onFailure {
                println("fail to campaign save")
                it.printStackTrace()
                launch {
                    snackBarStateGlobal.showSnackbar(it.message ?: "Fail to create")
                }
            }.onSuccess {
                scopeUi.nav.up()
            }
            showLoader = false
        }
    }

    fun activateDraft() {
        showLoader = true
        scope.launch {
            runCatching {
                state.entry?.extract()?.also {
                    val campaign = Repository.api.campaign.create(scopeUi.args.companyId, it)
                    Repository.api.campaign.changeState(
                        companyId = scopeUi.args.companyId,
                        campaignId = campaign.id,
                        newState = CampaignState.ACTIVE,
                        expectedImpressions = campaign.impressionExpected
                    )
                } ?: error("Invalid state of app, refresh required")
            }.onFailure {
                println("fail to campaign")
                it.printStackTrace()
                launch {
                    snackBarStateGlobal.showSnackbar(it.message ?: "Fail to create")
                }
            }.onSuccess {
                scopeUi.nav.up()
            }
            showLoader = false
        }
    }

    /**
     * - new campaigns, apply changes + activate
     * - old campaigns, create campaign + activate
     */
    fun activate() {
        showLoader = true
        scope.launch {
            runCatching {
                state.entry?.extract()?.also {
                    val campaign = Repository.api.campaign.create(scopeUi.args.companyId, it)
                    Repository.api.campaign.changeState(
                        companyId = scopeUi.args.companyId,
                        campaignId = campaign.id,
                        newState = CampaignState.ACTIVE,
                        expectedImpressions = campaign.impressionExpected
                    )
                } ?: error("Invalid state of app, refresh required")
            }.onFailure {
                println("fail to campaign")
                it.printStackTrace()
                launch {
                    snackBarStateGlobal.showSnackbar(it.message ?: "Fail to create")
                }
            }.onSuccess {
                scopeUi.nav.up()
            }
            showLoader = false
        }
    }


    fun change() {
        showLoader = true
        scope.launch {
            runCatching {
                state.entry?.extract()?.also {
                    Repository.api.campaign.changes(scopeUi.args.companyId, it)
                } ?: error("Invalid state of app, refresh required")
            }.onFailure {
                println("fail to campaign")
                it.printStackTrace()
                launch {
                    snackBarStateGlobal.showSnackbar(it.message ?: "Fail to change")
                }
            }.onSuccess {
                scopeUi.nav.up()
            }
            showLoader = false
        }
    }

    fun changeDraft() {
        showLoader = true
        scope.launch {
            runCatching {
                state.entry?.extract()?.also {
                    Repository.api.campaign.create(scopeUi.args.companyId, it)
                } ?: error("Invalid state of app, refresh required")
            }.onFailure {
                println("fail to campaign")
                it.printStackTrace()
                launch {
                    snackBarStateGlobal.showSnackbar(it.message ?: "Fail to change")
                }
            }.onSuccess {
                scopeUi.nav.up()
            }
            showLoader = false
        }
    }

    fun init(scopeUi: CampaignEditorScope) {
        this.scopeUi = scopeUi

        if (state is DataState.Result) return

        scope.launch {
            runCatching {

                val canAppView = scopeUi.login.canLayerBottomView(scopeUi.args.companyId)

                val dataJob = async {
                    if (scopeUi.args.campaignId > 0) {
                        Repository.api.campaign.single(scopeUi.args.companyId, scopeUi.args.campaignId)
                    } else {
                        Repository.api.campaign.template(scopeUi.args.companyId)
                    }
                }

                val appsJob = async {
                    runCatching {
                        if (canAppView) Repository.api.app.listingApps(scopeUi.args.companyId).apply {
                            items.removeAll { !AppType[it.type].usableInCampaign }
                        }
                        else error("can not view apps")
                    }.getOrNull()
                }

                val apps = appsJob.await() ?: Io.ListingApp()
                val campaign = dataJob.await()
                val isImpressionCampaign = CampaignType.IMPRESSION match campaign.settings.type

                tabs = buildList {
                    addAll(listOf(Tab.General00, Tab.Location01, Tab.Visual02))
                    if (isImpressionCampaign && campaign.otherCampaignGroups.isNotEmpty()) add(Tab.Offset03)
                    add(Tab.DynamicVisual04)
                    if (isImpressionCampaign && apps.items.isNotEmpty()) add(Tab.Application06)
                    add(Tab.MiniApp07)
                    add(Tab.Summary08)
                }

                state = DataState.Result(
                    CampaignWrapperE(
                        companyId = scopeUi.args.companyId,
                        data = campaign,
                        counter = FeatureCounter.get(scopeUi.args.companyId, campaign),
                        apps = apps
                    )
                )
            }
        }
    }

    private fun skipToSummary() {
        scope.launch {
            state.entry?.apply {
                extractErrors(
                    tabs.filterIndexed { index, item -> index <= tabIndex }
                )?.also {
                    snackBarStateGlobal.showSnackbar(it)
                } ?: run {
                    goToPage(tabs.lastIndex)
                }
            }
        }
    }

    private fun goToNextPage() {
        scope.launch {
            state.entry?.apply {
                extractErrors(
                    tabs.filterIndexed { index, item -> index <= tabIndex }
                )?.also {
                    snackBarStateGlobal.showSnackbar(it)
                } ?: run {
                    goToPage(tabIndex + 1)

                }
            }
        }
    }

    private fun goToPage(newPageIndex: Int) {
        if (newPageIndex < tabs.size) {
            when (tabs[newPageIndex]) {
                Tab.General00 -> Unit
                Tab.Location01 -> state.entry?.also {
                    makeDeviceUsage(it)
                }

                Tab.Visual02 -> scope.launch {
                    state.entry?.visual?.generatePickerArgs()
                }

                Tab.Offset03 -> Unit
                Tab.DynamicVisual04 -> scope.launch {
                    state.entry?.visualDynamic?.generatePickerArgs()
                }

                Tab.Effect05 -> TODO()
                Tab.Application06 -> Unit
                Tab.MiniApp07 -> Unit
                Tab.Summary08 -> Unit
            }
            tabIndex = newPageIndex
        }
    }

    override fun onRelease() {

    }

    private fun CampaignEditorVM.makeDeviceUsage(wrapper: CampaignWrapperE) {
        scope.launch {
            wrapper.location.counter.compute(wrapper.location)
            wrapper.location.unselectOverflowDevices()
        }
    }
}
